/**
 * 全画面共通のレイアウト(yojitsu-system と同方式)。
 * サイドメニュー: マスタ / 日次 / 月次 / スタッフ / 設定(未実装はプレースホルダ)。
 * ロール別表示(§3): admin=全メニュー / viewer=日次・月次の閲覧 / operator=スタッフのみ。
 */
import { useEffect, useState } from 'react';
import { IconMenu } from '@core/components/icons';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import { appEnv, firestoreDbId, getProjectId } from '@core/lib/firebase';
import { ROLE_LABELS } from '@core/lib/types';
import { useStackedTables } from '@core/lib/stackedTables';

type NavItem = { to: string; label: string; visible: boolean };

export const SCREEN_TITLES: Record<string, string> = {
  '/home': 'ダッシュボード',
  '/operators': 'オペレーターマスタ',
  '/companies': '会社マスタ',
  '/import': 'CSVインポート',
  '/bb-credentials': 'BB ID／パスワード一覧',
  '/pl': 'PL',
  '/summary/deliveries': '日報/サマリ(納品・人件費・交通費)',
  '/summary/companies': '会社別',
  '/summary/styles': '出勤形態×報酬区分別',
  '/summary/staff': '日次【集計】',
  '/staff': '本人成績',
  '/shift': 'シフト',
  '/settings/users': 'ユーザー管理',
  '/settings/system': 'システム設定',
};

export default function AppLayout() {
  const { user, role, signOut, isAdmin, isViewer, isOperator } = useAuth();
  const { pathname } = useLocation();
  /** スマホのドロワーメニュー。ルート遷移で自動的に閉じる */
  const [menuOpen, setMenuOpen] = useState(false);
  /** PCのメニューバーの表示/非表示(2026-09-10 指示)。次回も同じ状態で開く */
  const [sideHidden, setSideHidden] = useState(() => {
    try {
      return localStorage.getItem('cc.sideHidden') === '1';
    } catch {
      return false;
    }
  });
  const toggleSide = () =>
    setSideHidden((v) => {
      try {
        localStorage.setItem('cc.sideHidden', v ? '0' : '1');
      } catch {
        /* プライベートブラウズなどで保存できなくても表示は切り替える */
      }
      return !v;
    });
  /** スマホでは全ての表を縦積みカードにする(yojitsu-system 共通方式) */
  useStackedTables();

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  /** 管理者のログイン直後の画面(2026-09-12 指示) */
  const topItems: NavItem[] = [{ to: '/home', label: 'ダッシュボード', visible: isAdmin }];
  const masterItems: NavItem[] = [
    { to: '/operators', label: 'オペレーターマスタ', visible: isAdmin || isViewer },
    { to: '/companies', label: '会社マスタ', visible: isAdmin || isViewer },
    { to: '/bb-credentials', label: 'BB ID／パスワード一覧', visible: isAdmin },
    { to: '/import', label: 'CSVインポート', visible: isAdmin },
  ];
  /** 集計(⑥指示): 各ページ内で日次/週次/月次を切り替える */
  const summaryItems: NavItem[] = [
    { to: '/pl', label: 'PL', visible: isAdmin || isViewer },
    { to: '/summary/companies', label: '会社別', visible: isAdmin || isViewer },
    { to: '/summary/styles', label: '出勤形態×報酬区分別', visible: isAdmin || isViewer },
    // 日報/サマリ(旧 納品件数の入力)は日次【集計】の上(2026-09-10 指示)
    { to: '/summary/deliveries', label: '日報/サマリ', visible: isAdmin },
    { to: '/summary/staff', label: '日次【集計】', visible: isAdmin || isViewer },
  ];
  const staffItems: NavItem[] = [
    { to: '/staff', label: '本人成績', visible: isAdmin || isOperator },
    { to: '/shift', label: 'シフト', visible: isAdmin || isOperator },
  ];
  const settingItems: NavItem[] = [
    { to: '/settings/users', label: 'ユーザー管理', visible: isAdmin },
    { to: '/settings/system', label: 'システム設定', visible: isAdmin },
  ];

  const title = SCREEN_TITLES[pathname] ?? 'cc-system';

  const renderSection = (label: string, items: NavItem[]) => {
    const visible = items.filter((i) => i.visible);
    if (visible.length === 0) return null;
    return (
      <>
        <div className="side-sec">{label}</div>
        {visible.map((i) => (
          <NavLink key={i.to} to={i.to} className={({ isActive }) => `side-item${isActive ? ' on' : ''}`}>
            {i.label}
          </NavLink>
        ))}
      </>
    );
  };

  return (
    <div className={`app${menuOpen ? ' menu-open' : ''}${sideHidden ? ' side-hidden' : ''}`}>
      <aside>
        <div className="side-brand">
          <div className="brand">SC NEXUS / CC DIVISION</div>
          <div className="side-title">cc-system</div>
          {/* バージョン(package.json)とビルド時刻。どの版が動いているかの確認用(2026-09-10 指示) */}
          <div className="side-version">
            v{__APP_VERSION__} <span>build {__BUILD_ID__}</span>
          </div>
        </div>

        {/* 並びは 2026-09-12 指示: ダッシュボード → 集計 → スタッフ → マスタ → 設定 */}
        {renderSection('ダッシュボード', topItems)}
        {renderSection('集計', summaryItems)}
        {renderSection('スタッフ', staffItems)}
        {renderSection('マスタ', masterItems)}
        {renderSection('設定', settingItems)}

        <div className="side-foot">
          <button className="side-signout" onClick={() => void signOut()}>
            ログアウト
          </button>
        </div>
      </aside>
      {/* ドロワーの背景(タップで閉じる) */}
      <div className="side-overlay" onClick={() => setMenuOpen(false)} />

      <main>
        {/* テスト環境の帯。手元の開発サーバーと、テスト用データベースに繋いだビルドで出す(D-65)。
            本番((default))と見分けが付かないまま操作するのを防ぐ */}
        {(firestoreDbId !== '(default)' || (import.meta.env.DEV && appEnv === 'dev')) && (
          <div className="envband">
            テスト環境({getProjectId()}
            {firestoreDbId !== '(default)' ? ` / データベース ${firestoreDbId}` : ''})に接続しています — 本番データではありません
          </div>
        )}

        <div className="topbar">
          {/* スマホのみ表示のメニューボタン(ドロワーを開く) */}
          <button className="menu-btn" aria-label="メニュー" onClick={() => setMenuOpen(true)}>
            <IconMenu size={18} />
          </button>
          {/* PC: メニューバーの表示/非表示(2026-09-10 指示) */}
          <button className="side-toggle" aria-label="メニューバーの表示/非表示" title="メニューバーの表示/非表示" onClick={toggleSide}>
            <IconMenu size={18} />
          </button>
          <h2>{title}</h2>
          <span className="user">
            {user?.name}
            <span className="user-role"> ・{role ? ROLE_LABELS[role] : ''}</span>
          </span>
        </div>

        <div className="content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
